import { IconImageM } from "@common/components/icons/IconsM";
import { ModalM } from '@common/components/modal/ModalM';
import TextM from '@common/components/text/TextM';
import { theme } from '@common/theme/theme';
import React, { useMemo, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { ActivityIndicator, Badge } from 'react-native-paper';
import { FontAwesome5 } from "@expo/vector-icons";
import SchoolSubjectModal from "../../../student/organize/school-subjects/SchoolSubjectModal";
import { filterSchoolSubjectsByClass } from '@data/utility/Utils';

const subjectsColors = [theme.colors.accentLighter, theme.colors.primaryLighter, theme.colors.successLight];

const SchoolSubjectSelector = ({
                                   schoolSubjects,
                                   onSubjectsChange = () => {
                                   },
                                   setSubjects,
                                   subjects,
                                   maxSelection = 3,
                                   loading = false,
                                   studentClass = null,
                                   style = {},
                                   singleSelect = false, // New prop for single select mode
                                   showConfirmButton = true, // New prop to control confirm button visibility
                                   compactWidth = false,
                                   chapterStyle = false,
                               }) => {
    const [modalVisible, setModalVisible] = useState(false);

    // Filter options by min_student_class if provided
    const filteredOptions = useMemo(() => {
        return filterSchoolSubjectsByClass(schoolSubjects, studentClass)
    }, [schoolSubjects, studentClass])

    const handleSelectSubject = (selectedId) => {
        const selectedSubject = filteredOptions.find((subject) => subject.value === selectedId);

        if (selectedSubject) {
            if (singleSelect) {
                // Single select mode: always replace current selection and close modal
                const updatedSubjects = [{
                    id: selectedSubject.value,
                    value: selectedSubject.value,
                    label: selectedSubject.label,
                    icon: selectedSubject.icon,
                }];
                setSubjects(updatedSubjects);
                onSubjectsChange(updatedSubjects);
                setModalVisible(false); // Close modal immediately
            } else {
                // Multi-select mode: existing behavior
                const isAlreadySelected = subjects.some((s) => s.value === selectedSubject.value);

                if (isAlreadySelected) {
                    const updatedSubjects = subjects.filter((s) => s.value !== selectedSubject.value);
                    setSubjects(updatedSubjects);
                    onSubjectsChange(updatedSubjects);
                } else if (subjects.length < maxSelection) {
                    const updatedSubjects = [
                        ...subjects,
                        {
                            id: selectedSubject.value,
                            value: selectedSubject.value,
                            label: selectedSubject.label,
                            icon: selectedSubject.icon,
                        },
                    ];
                    setSubjects(updatedSubjects);
                    onSubjectsChange(updatedSubjects);
                }
            }
        }
    };

    return (
        <View style={{}}>
            {loading ? (
                <ActivityIndicator/>
            ) : (
                <>
                    {chapterStyle && singleSelect ? (
                        subjects.length > 0 ? (
                            <TouchableOpacity
                                onPress={() => setModalVisible(true)}
                                style={{
                                    backgroundColor: theme.colors.white,
                                    borderWidth: 1,
                                    borderColor: theme.colors.primary,
                                    borderRadius: 15,
                                    padding: 12,
                                    flexDirection: "row",
                                    alignItems: "center",
                                    marginVertical: 5,
                                    elevation: 1,
                                    ...style,
                                }}
                            >
                                {subjects[0]?.icon && (
                                    <IconImageM
                                        uri={subjects[0].icon}
                                        width={28}
                                        style={{ marginRight: 10 }}
                                    />
                                )}
                                <View style={{ flex: 1 }}>
                                    <TextM
                                        color={theme.colors.primary}
                                        fontWeight="SemiBold"
                                        fontSize={13}
                                    >
                                        {subjects[0].label}
                                    </TextM>
                                    <TextM
                                        fontSize={11}
                                        color={theme.colors.grey}
                                        style={{ marginTop: 1 }}
                                    >
                                        Matière sélectionnée
                                    </TextM>
                                </View>
                            </TouchableOpacity>
                        ) : (
                            <TouchableOpacity
                                onPress={() => setModalVisible(true)}
                                style={{
                                    alignSelf : "end" ,
                                    borderRadius: 15,
                                    paddingVertical: 8,
                                    paddingHorizontal: 15,
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    maxWidth: compactWidth ? 180 : 'auto',
                                    elevation: 1,
                                    backgroundColor: theme.colors.accent,
                                    borderWidth: 1,
                                    borderColor: theme.colors.accent,
                                    marginVertical: 5,
                                    ...style,
                                }}
                            >
                                {loading ? (
                                    <ActivityIndicator size="small" color={theme.colors.white} style={{ marginRight: 8 }} />
                                ) : (
                                    <FontAwesome5
                                        name="graduation-cap"
                                        size={12}
                                        color={theme.colors.white}
                                        style={{ marginRight: 8 }}
                                    />
                                )}
                                <TextM
                                    color={theme.colors.white}
                                    fontWeight="Bold"
                                    fontSize={12}
                                    numberOfLines={1}
                                    style={{ flex: 1 }}
                                >
                                    {loading ? 'Mise à jour...' : 'Choisir une matière'}
                                </TextM>
                            </TouchableOpacity>
                        )
                    ) : (
                        // Default rendering (original style)
                        <TouchableOpacity
                            onPress={() => setModalVisible(true)}
                            style={{
                                margin: 5,
                                height: 45,
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderRadius: theme.roundness,
                                paddingVertical: 5,
                                paddingHorizontal: 15,
                                borderWidth: 1,
                                borderColor: theme.colors.lightMore,
                                backgroundColor: theme.colors.white,
                                ...style,
                            }}
                        >
                            {subjects.length > 0 ? (
                                singleSelect ? (
                                    // Single select display: simpler, no badge
                                    <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
                                        <IconImageM uri={subjects[0]?.icon} width={20} style={{}}/>
                                        <TextM fontSize={14} color={theme.colors.grey}>{subjects[0].label}</TextM>
                                    </View>
                                ) : (
                                    // Multi-select display: badges
                                    <View style={{flexDirection: 'row', gap: 3, alignItems: 'center'}}>
                                        {subjects.map((s, index) => (
                                            <Badge
                                                key={s.id}
                                                style={{paddingHorizontal: 5, backgroundColor: s?.color ?? subjectsColors[index]}}
                                            >
                                                <View style={{
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    gap: 3,
                                                }}>
                                                    <IconImageM uri={s?.icon} width={18} style={{}}/>
                                                    <TextM fontSize={12}>{s.label}</TextM>
                                                </View>
                                            </Badge>
                                        ))}
                                    </View>
                                )
                            ) : (
                                <TextM color={theme.colors.grey} fontSize={10}>
                                    {singleSelect ? "Choisir une matière" : "Choisir vos matières"}
                                </TextM>
                            )}
                        </TouchableOpacity>
                    )}
                    <ModalM visible={modalVisible} onDismiss={() => setModalVisible(false)} style={{height: '90%'}}>
                        <SchoolSubjectModal
                            onSelectSchoolSubject={handleSelectSubject}
                            options={filteredOptions?.map(x => ({
                                ...x, 
                                disabled: singleSelect ? false : (!subjects?.map(s => s.value)?.includes(x.value) && subjects.length >= maxSelection)
                            }))}
                            setVisible={setModalVisible}
                            selectedSubjects={subjects.map((subject) => subject.value)}
                            title={singleSelect ? "Choisir une matière" : "Choisir vos matières"}
                            showExitBtn
                            showConfirmButton={showConfirmButton}
                        />
                    </ModalM>
                </>
            )}
        </View>
    );
};

export default SchoolSubjectSelector;
